import { useId, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useOptionalDock } from './context';
import type { ToolWindowBadge } from './registry';

/**
 * Provides a tool window's content on the current page (spec 6.8.2). While this is mounted the window is
 * available; its children are rendered into the window (keeping the page's React context and state) only
 * while the window is shown. `badge` feeds the window's tab and status-bar button.
 */
export function ToolWindowContent({
  window: id,
  badge,
  children,
}: {
  window: string;
  badge?: ToolWindowBadge;
  children: ReactNode;
}) {
  const dock = useOptionalDock();
  const token = useId();
  const register = dock?.register;
  const setBadge = dock?.setBadge;
  const badgeKey = JSON.stringify(badge ?? {});

  useLayoutEffect(() => register?.(id, token), [register, id, token]);
  useLayoutEffect(() => {
    setBadge?.(id, token, JSON.parse(badgeKey) as ToolWindowBadge);
  }, [setBadge, id, token, badgeKey]);

  if (!dock || dock.provider(id) !== token || !dock.effective.displayed.has(id)) return null;
  return createPortal(children, dock.host(id));
}
